import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, authApi } from '@/lib/api'
import { Card } from '@/components/ui/Card'
import { DataTable, type Column } from '@/components/ui/DataTable'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { ErrorState } from '@/components/ui/ErrorState'
import { PiiField } from '@/components/ui/PiiField'
import { Modal } from '@/components/ui/Modal'
import { usePresentation } from '@/components/layout/AppShell'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'

interface UserRow {
  user_id: string
  email: string
  name: string | null
  tier: string
  access: 'free' | 'trial' | 'paid'
  daily_streak: number
  total_days_active: number
  last_activity_date: string | null
  stripe_mrr: number | null
  created_at: number | null
}

interface UsersResponse {
  users: UserRow[]
  total: number
  page: number
  totalPages: number
  pageSize: number
  hasNext: boolean
  hasPrevious: boolean
}

type SortField = 'created_at' | 'email' | 'name' | 'tier' | 'stripe_mrr'
type AccessFilter = '' | 'free' | 'trial' | 'paid'

const PAGE_SIZE = 50

const tierBadgeVariant: Record<string, 'brand' | 'success' | 'warning' | 'default'> = {
  admin: 'brand',
  paid: 'success',
  lifetime: 'success',
  group: 'success',
  trial: 'warning',
  free: 'default',
}

export function UserListPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { presenting } = usePresentation()
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search.trim(), 300)
  const [page, setPage] = useState(1)
  const [sortField, setSortField] = useState<SortField>('created_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')
  const [accessFilter, setAccessFilter] = useState<AccessFilter>('')
  const [grantModalOpen, setGrantModalOpen] = useState(false)
  const [grantEmail, setGrantEmail] = useState('')
  const [grantDays, setGrantDays] = useState(14)
  const [granting, setGranting] = useState(false)
  const [grantResult, setGrantResult] = useState<{ success: boolean; message: string } | null>(null)

  const lastSuccessfulData = useRef<UsersResponse | null>(null)
  const { data, isPending, isFetching, isError, isPlaceholderData, refetch } = useQuery({
    queryKey: ['admin-users', { search: debouncedSearch, page, sortField, sortOrder, accessFilter }],
    queryFn: ({ signal }) =>
      api.get<UsersResponse>('/api/admin/users', {
        signal,
        params: {
          page,
          limit: PAGE_SIZE,
          search: debouncedSearch || undefined,
          access: accessFilter || undefined,
          sort: sortField,
          order: sortOrder,
        },
      }),
    placeholderData: keepPreviousData,
  })

  useEffect(() => {
    if (data && !isPlaceholderData && !isError) lastSuccessfulData.current = data
  }, [data, isError, isPlaceholderData])

  const displayedData = data ?? lastSuccessfulData.current

  const handleSort = useCallback((field: string, order: 'asc' | 'desc') => {
    setSortField(field as SortField)
    setSortOrder(order)
    setPage(1)
  }, [])

  const columns: Column<UserRow>[] = [
    {
      id: 'email',
      header: 'Email',
      sortable: true,
      cell: (row) => <PiiField value={row.email} presenting={presenting} className="font-medium" />,
    },
    {
      id: 'name',
      header: 'Name',
      sortable: true,
      cell: (row) => <PiiField value={row.name} presenting={presenting} />,
    },
    {
      id: 'tier',
      header: 'Tier',
      sortable: true,
      cell: (row) => <Badge variant={tierBadgeVariant[row.tier] ?? 'default'}>{row.tier}</Badge>,
    },
    {
      id: 'daily_streak',
      header: 'Streak',
      cell: (row) => <span className="tabular-nums">{row.daily_streak}</span>,
      className: 'text-right',
    },
    {
      id: 'total_days_active',
      header: 'Days Active',
      cell: (row) => <span className="tabular-nums">{row.total_days_active}</span>,
      className: 'text-right',
    },
    {
      id: 'stripe_mrr',
      header: 'MRR',
      sortable: true,
      cell: (row) =>
        row.stripe_mrr !== null ? (
          <span className="tabular-nums">${(row.stripe_mrr / 100).toFixed(2)}</span>
        ) : (
          <span className="text-[var(--content-tertiary)]">--</span>
        ),
      className: 'text-right',
    },
    {
      id: 'last_activity_date',
      header: 'Last Active',
      cell: (row) =>
        row.last_activity_date ? (
          <span className="text-[var(--content-secondary)] tabular-nums">{row.last_activity_date}</span>
        ) : (
          <span className="text-[var(--content-tertiary)]">Never</span>
        ),
    },
    {
      id: 'created_at',
      header: 'Joined',
      sortable: true,
      cell: (row) =>
        row.created_at ? (
          <span className="text-[var(--content-secondary)] tabular-nums">
            {new Date(row.created_at * 1000).toLocaleDateString()}
          </span>
        ) : (
          <span className="text-[var(--content-tertiary)]">--</span>
        ),
    },
  ]

  const hasActiveFilters = Boolean(search || accessFilter)

  function clearFilters() {
    setSearch('')
    setAccessFilter('')
    setPage(1)
  }

  async function handleGrantByEmail() {
    if (!grantEmail.trim()) return
    setGranting(true)
    setGrantResult(null)
    try {
      const res = await authApi.post<{ success: boolean; userId: string; created: boolean; expiresAt: number }>(
        '/api/v1/admin/grant-temp-access',
        { email: grantEmail.trim(), durationDays: grantDays },
      )
      const expiryDate = new Date(res.expiresAt * 1000).toLocaleDateString()
      setGrantResult({
        success: true,
        message: res.created
          ? `Account created and ${grantDays}-day access granted (expires ${expiryDate}). They can log in via magic link.`
          : `${grantDays}-day access granted to existing user (expires ${expiryDate}).`,
      })
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      setGrantEmail('')
    } catch (e) {
      setGrantResult({ success: false, message: String(e) })
    } finally {
      setGranting(false)
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-heading font-semibold">Users</h1>
        <div className="flex items-center gap-3">
          <button
            onClick={() => { setGrantModalOpen(true); setGrantResult(null) }}
            className="px-4 py-2 text-sm rounded-full bg-[#65A0A8] text-white hover:bg-[#5a9098] transition-colors cursor-pointer"
          >
            Grant Access by Email
          </button>
          {displayedData && (
            <span className="text-sm text-[var(--content-tertiary)]">
              {displayedData.total.toLocaleString()} total
            </span>
          )}
        </div>
      </div>

      <Modal open={grantModalOpen} onClose={() => setGrantModalOpen(false)} title="Grant Temporary Access">
        <p className="text-sm text-[var(--content-secondary)] mb-4">
          Grant content access by email. If the person doesn't have an account yet, one will be created automatically. No emails are sent — tell them to log in at voicescience.org when ready.
        </p>
        <div className="space-y-3">
          <div>
            <label className="block text-xs text-[var(--content-tertiary)] mb-1">Email</label>
            <input
              type="email"
              value={grantEmail}
              onChange={(e) => setGrantEmail(e.target.value)}
              placeholder="user@example.com"
              className="w-full px-3 py-2 text-sm bg-[var(--surface-nested)] border border-[var(--border)] rounded-lg text-[var(--content)] placeholder:text-[var(--content-tertiary)] outline-none focus:ring-2 focus:ring-[var(--brand)]/30"
            />
          </div>
          <div>
            <label className="block text-xs text-[var(--content-tertiary)] mb-1">Duration</label>
            <select
              value={grantDays}
              onChange={(e) => setGrantDays(Number(e.target.value))}
              className="w-full px-3 py-2 text-sm bg-[var(--surface-nested)] border border-[var(--border)] rounded-lg text-[var(--content)] outline-none cursor-pointer"
            >
              <option value={7}>7 days</option>
              <option value={14}>14 days</option>
              <option value={30}>30 days</option>
              <option value={60}>60 days</option>
              <option value={90}>90 days</option>
            </select>
          </div>
          {grantResult && (
            <p className={`text-xs ${grantResult.success ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
              {grantResult.message}
            </p>
          )}
          <button
            onClick={handleGrantByEmail}
            disabled={granting || !grantEmail.trim()}
            className="w-full px-4 py-2 text-sm rounded-full bg-[#65A0A8] text-white hover:bg-[#5a9098] disabled:opacity-50 transition-colors cursor-pointer"
          >
            {granting ? 'Granting...' : 'Grant Access'}
          </button>
        </div>
      </Modal>

      <Card className="mb-4">
        <div className="px-4 py-3 flex flex-wrap items-end gap-4">
          <div className="min-w-64 flex-1">
            <label htmlFor="user-directory-search" className="mb-1 block text-xs font-semibold text-[var(--content-secondary)]">
              Search users
            </label>
            <input
              id="user-directory-search"
              type="search"
              placeholder="Email, name, or user ID"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
                setPage(1)
              }}
              className="w-full px-3 py-2 text-sm bg-[var(--surface-nested)] border border-[var(--border)] rounded-lg text-[var(--content)] placeholder:text-[var(--content-tertiary)] outline-none focus:ring-2 focus:ring-[var(--brand)]/30"
            />
          </div>
          <div>
            <label htmlFor="user-directory-access" className="mb-1 block text-xs font-semibold text-[var(--content-secondary)]">
              Access
            </label>
            <select
              id="user-directory-access"
              aria-describedby="user-directory-access-description"
              value={accessFilter}
              onChange={(event) => {
                setAccessFilter(event.target.value as AccessFilter)
                setPage(1)
              }}
              className="min-w-36 px-3 py-2 text-sm bg-[var(--surface-nested)] border border-[var(--border)] rounded-lg text-[var(--content)] outline-none cursor-pointer"
            >
              <option value="">All access</option>
              <option value="free">Free</option>
              <option value="trial">Trial</option>
              <option value="paid">Paid</option>
            </select>
            <span id="user-directory-access-description" className="sr-only">
              Membership grouping for this directory. Paid includes paid, lifetime, admin, and group memberships; authorization is enforced separately.
            </span>
          </div>
          {hasActiveFilters && (
            <Button type="button" variant="ghost" size="sm" onClick={clearFilters}>
              Clear filters
            </Button>
          )}
        </div>
      </Card>

      <Card>
        {isError && !displayedData ? (
          <ErrorState
            title="User directory unavailable"
            description="The user directory could not be loaded. Check your connection and try again."
            action={<Button onClick={() => void refetch()}>Try again</Button>}
          />
        ) : (
          <>
            {isError && displayedData && (
              <div className="flex items-center justify-between gap-3 border-b border-red-200 bg-red-50 px-4 py-2 text-xs text-red-800" role="alert">
                <span>Could not refresh the directory. Showing the previous results.</span>
                <Button size="sm" variant="ghost" onClick={() => void refetch()}>
                  Try again
                </Button>
              </div>
            )}
            {isFetching && !isPending && (
              <div className="border-b border-[var(--border)] px-4 py-2 text-xs text-[var(--content-tertiary)]" role="status">
                Updating users…
              </div>
            )}
            <DataTable
              columns={columns}
              data={displayedData?.users ?? []}
              keyExtractor={(row) => row.user_id}
              onRowClick={(row) => navigate(`/users/${row.user_id}`)}
              rowLabel={(row) =>
                presenting
                  ? 'View masked user details'
                  : `View user details for ${row.name?.trim() || row.email || row.user_id}`
              }
              sortField={sortField}
              sortOrder={sortOrder}
              onSort={handleSort}
              page={displayedData?.page}
              totalPages={displayedData?.totalPages}
              totalItems={displayedData?.total}
              pageSize={displayedData?.pageSize}
              itemLabel="users"
              onPageChange={setPage}
              loading={isPending}
              fetching={isFetching && !isPending}
              emptyMessage={hasActiveFilters ? 'No users match these filters' : 'No user accounts yet'}
            />
          </>
        )}
      </Card>
    </div>
  )
}
